import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { FileText, X, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import Card from '../Card';
import Skeleton from '../ui/Skeleton';
import ServiceConfigDrawer, { type ServiceConfigSubmission } from './ServiceConfigDrawer';
import ClientServiceExplorer, {
  type ClientServiceExplorerSelectionSeed,
} from './ClientServiceExplorer';
import {
  cancelClientServiceProposal,
  changeCurrentClientService,
  createClientServiceCheckoutSession,
  createClientServiceProposal,
  fetchServiceCheckoutSessionStatus,
  fetchServiceWorkspaceCatalog,
  fetchServiceWorkspaceSummary,
  finalizeCurrentClientServiceChange,
  markClientServiceProposalViewed,
  rejectClientServiceProposal,
  type ServiceCatalogService,
  type ServiceWorkspaceCurrentService,
  type ServiceWorkspaceProposal,
  type ServiceWorkspaceSummary,
} from '../../lib/clientServices';

type ServiceWorkspaceProps = {
  mode: 'client' | 'staff';
  orgId?: string;
  organizationName?: string | null;
  refreshNonce?: number;
  compact?: boolean;
};

type DrawerState =
  | null
  | {
      mode: 'checkout' | 'proposal';
      initialServiceCode?: string | null;
      initialTierSkuId?: string | null;
      initialOfferingLabels?: string[];
      initialAddOnNames?: string[];
      initialTitle?: string | null;
      initialSummary?: string | null;
      initialProposalType?: string | null;
    };

type BannerState = {
  tone: 'success' | 'warn' | 'danger' | 'neutral';
  title: string;
  message: string;
};

const Shell = styled.div<{ $compact?: boolean }>`
  display: grid;
  gap: ${({ $compact }) => ($compact ? '14px' : '18px')};
`;

const EmbeddedExplorerFrame = styled.div<{ $compact?: boolean }>`
  display: grid;
  padding: ${({ $compact }) => ($compact ? '18px 14px 14px' : '12px 0 0')};

  @media (max-width: 920px) {
    padding: ${({ $compact }) => ($compact ? '14px 10px 12px' : '10px 0 0')};
  }
`;

const HeaderCard = styled(Card)`
  display: grid;
  gap: 14px;
  padding: 18px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

const HeaderTitleWrap = styled.div`
  display: grid;
  gap: 5px;
`;

const Eyebrow = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.22px;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 0;
  color: #f3f4f6;
  font-size: clamp(1.3rem, 2vw, 1.8rem);
  line-height: 1.05;
  font-weight: 900;
`;

const Sub = styled.p`
  margin: 0;
  color: #b6c0ce;
  font-size: 13px;
  line-height: 1.6;
  font-weight: 700;
  max-width: 70ch;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const OrgSelectorRow = styled.div`
  display: grid;
  gap: 14px;
  align-items: start;
  grid-template-columns: minmax(0, 360px);
  margin-bottom: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const OrgSelectorControl = styled.div`
  display: grid;
  gap: 8px;
  justify-items: start;
`;

const OrgSelectorLabel = styled.label`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.22px;
  text-transform: uppercase;
`;

const OrgSelectorField = styled.select`
  width: min(360px, 100%);
  min-height: 48px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #f3f4f6;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 800;
  outline: none;

  &:focus {
    border-color: rgba(224, 161, 94, 0.36);
  }

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }

  
`;

const ProposalAwaitList = styled.div`
  display: grid;
  gap: 12px;
`;

const proposalPulse = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(224,161,94,.08);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(224,161,94,0);
  }
`;

const ProposalAwaitCard = styled.div<{ $active?: boolean }>`
  position: relative;
  width: 100%;
  min-height: 88px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(224,161,94,.28)' : 'rgba(255,255,255,.09)'};
  background: ${({ $active }) =>
    $active ? 'rgba(224,161,94,.08)' : 'rgba(255,255,255,.025)'};
  color: #f3f4f6;
  box-shadow: ${({ $active }) =>
    $active ? '0 10px 24px rgba(224,161,94,.08)' : 'none'};
  display: block;
  transition:
    border-color 160ms ease,
    background 160ms ease,
    box-shadow 160ms ease;
  animation: ${({ $active }) =>
    $active ? 'none' : css`${proposalPulse} 2.8s ease-in-out infinite`};

  &:hover {
    border-color: rgba(224,161,94,.22);
    background: rgba(255,255,255,.04);
  }
`;

const ProposalAwaitBody = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  display: grid;
  gap: 6px;
  padding: 0;
  width: 100%;
  cursor: pointer;
`;

const ProposalAwaitEyebrow = styled.div`
  color: rgba(246,195,126,.86);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const ProposalAwaitTitle = styled.div`
  color: #f3f4f6;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.35;
`;

const ProposalAwaitCopy = styled.div`
  color: #a9b4c4;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
`;

const ProposalDismissButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: rgba(255,255,255,.05);
  color: rgba(243,244,246,.68);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 160ms ease,
    color 160ms ease;

  &:hover {
    background: rgba(255,255,255,.1);
    color: #f3f4f6;
  }
`;

const ProposalDeclineButton = styled.button`
  position: absolute;
  right: 16px;
  bottom: 14px;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.03);
  color: rgba(243,244,246,.78);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .16em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background 160ms ease,
    color 160ms ease;

  &:hover:not(:disabled) {
    border-color: rgba(255,110,110,.26);
    background: rgba(255,110,110,.08);
    color: #ffd2d2;
  }

  &:disabled {
    opacity: .55;
    cursor: not-allowed;
  }
`;

const WorkspaceSkeletonPanel = styled(Card)`
  display: grid;
  gap: 14px;
  padding: 18px;
`;

const WorkspaceSkeletonGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const WorkspaceSkeletonCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const ActionButton = styled.button<{ $accent?: boolean; $danger?: boolean }>`
  min-height: 38px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid
    ${({ $accent, $danger }) =>
      $danger
        ? 'rgba(255,110,110,.3)'
        : $accent
          ? 'rgba(224,161,94,.42)'
          : 'rgba(255,255,255,.14)'};
  background: ${({ $accent, $danger }) =>
    $danger
      ? 'rgba(255,110,110,.12)'
      : $accent
        ? 'rgba(224,161,94,.14)'
        : 'rgba(255,255,255,.04)'};
  color: #f3f4f6;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Section = styled(Card)`
  display: grid;
  gap: 14px;
  padding: 18px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SectionTitleWrap = styled.div`
  display: grid;
  gap: 4px;
`;

const SectionTitle = styled.div`
  color: #f3f4f6;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;

const SectionSub = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
`;

const Notice = styled.div<{ $tone?: 'warn' | 'danger' | 'success' | 'neutral' }>`
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid
    ${({ $tone = 'neutral' }) =>
      $tone === 'danger'
        ? 'rgba(255,110,110,.26)'
        : $tone === 'warn'
          ? 'rgba(224,161,94,.3)'
          : $tone === 'success'
            ? 'rgba(86,211,173,.28)'
            : 'rgba(255,255,255,.1)'};
  background:
    ${({ $tone = 'neutral' }) =>
      $tone === 'danger'
        ? 'rgba(255,110,110,.09)'
        : $tone === 'warn'
          ? 'rgba(224,161,94,.1)'
          : $tone === 'success'
            ? 'rgba(86,211,173,.1)'
            : 'rgba(255,255,255,.04)'};
  color: #e8eaed;
  font-size: 12px;
  line-height: 1.55;
  font-weight: 700;
`;

const Banner = styled(Notice)`
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border-color: ${({ $tone = 'neutral' }) =>
    $tone === 'danger'
      ? 'rgba(255,110,110,.2)'
      : $tone === 'warn'
        ? 'rgba(224,161,94,.22)'
        : 'rgba(255,255,255,.09)'};
  background: ${({ $tone = 'neutral' }) =>
    $tone === 'danger'
      ? 'rgba(255,110,110,.06)'
      : $tone === 'warn'
        ? 'rgba(224,161,94,.07)'
        : 'rgba(255,255,255,.025)'};
  color: #dde4ef;
`;

const BannerTitle = styled.div`
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const ServiceCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ServiceName = styled.div`
  color: #f3f4f6;
  font-size: 15px;
  font-weight: 900;
`;

const ServiceMeta = styled.div`
  margin-top: 4px;
  color: #9aa4b2;
  font-size: 12px;
  line-height: 1.55;
  font-weight: 700;
`;

const StatusPill = styled.span<{ $tone?: 'good' | 'warn' | 'neutral' }>`
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? 'rgba(86,211,173,.3)'
        : $tone === 'warn'
          ? 'rgba(224,161,94,.32)'
          : 'rgba(255,255,255,.14)'};
  background:
    ${({ $tone = 'neutral' }) =>
      $tone === 'good'
        ? 'rgba(86,211,173,.1)'
        : $tone === 'warn'
          ? 'rgba(224,161,94,.12)'
          : 'rgba(255,255,255,.04)'};
  color: #f3f4f6;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const MetaGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const MetaCell = styled.div`
  display: grid;
  gap: 4px;
`;

const MetaLabel = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const MetaValue = styled.div`
  color: #f3f4f6;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 800;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Pill = styled.span`
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #dbe3ef;
  font-size: 11px;
  font-weight: 800;
`;

const ServiceCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProposalList = styled.div`
  display: grid;
  gap: 12px;
`;

const ProposalCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const ProposalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ProposalTitle = styled.div`
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 900;
`;

const ProposalSub = styled.div`
  margin-top: 4px;
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.55;
  font-weight: 700;
`;

const ProposalMetrics = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ProposalMetric = styled.div`
  display: grid;
  gap: 4px;
`;

const ProposalMetricLabel = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const ProposalMetricValue = styled.div`
  color: #f3f4f6;
  font-size: 16px;
  line-height: 1;
  font-weight: 900;
`;

const CatalogGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const CatalogCard = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  padding: 14px;
  display: grid;
  gap: 12px;
`;

const CatalogTitle = styled.div`
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 900;
`;

const CatalogCopy = styled.div`
  color: #b9c4d1;
  font-size: 12px;
  line-height: 1.6;
  font-weight: 700;
`;

const HistoryList = styled.div`
  display: grid;
  gap: 10px;
`;

const HistoryRow = styled.div`
  display: grid;
  gap: 4px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeCode(value: string | null | undefined) {
  return cleanText(value).toLowerCase();
}

function formatCurrency(cents: number, currency = 'usd') {
  const amount = Number.isFinite(cents) ? cents / 100 : 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

function formatDateLabel(value: string | null | undefined) {
  const text = cleanText(value);
  if (!text) return '--';
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatHoursBand(minMinutes: number | null | undefined, maxMinutes: number | null | undefined) {
  const minHours = Number.isFinite(Number(minMinutes)) && Number(minMinutes) > 0 ? Number(minMinutes) / 60 : null;
  const maxHours = Number.isFinite(Number(maxMinutes)) && Number(maxMinutes) > 0 ? Number(maxMinutes) / 60 : null;
  const normalize = (hours: number) => {
    const rounded = Math.round(hours * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  };
  if (minHours !== null && maxHours !== null) {
    return minHours === maxHours
      ? `${normalize(minHours)}h / mo`
      : `${normalize(minHours)}-${normalize(maxHours)}h / mo`;
  }
  if (minHours !== null) return `${normalize(minHours)}h+ / mo`;
  if (maxHours !== null) return `Up to ${normalize(maxHours)}h / mo`;
  return '--';
}

function proposalTone(status: string): 'good' | 'warn' | 'neutral' {
  const normalized = normalizeCode(status);
  if (normalized === 'applied') return 'good';
  if (normalized === 'sent' || normalized === 'viewed' || normalized === 'checkout_started') return 'warn';
  return 'neutral';
}

function serviceStatusTone(status: string): 'good' | 'warn' | 'neutral' {
  const normalized = normalizeCode(status);
  if (normalized === 'active') return 'good';
  if (normalized === 'trialing') return 'warn';
  return 'neutral';
}

function getServiceKey(service: ServiceCatalogService) {
  return normalizeCode(service.code || service.category);
}

function getTierStartingPrice(service: ServiceCatalogService) {
  const amounts = service.tiers
    .map((tier) => Number(tier.amountCents || 0))
    .filter((value) => Number.isFinite(value) && value >= 0);
  if (!amounts.length) return null;
  return Math.min(...amounts);
}

function getOrganizationOptionLabel(option: {
  businessName: string | null;
  name: string;
}) {
  return cleanText(option.businessName) || cleanText(option.name) || 'Organization';
}

function buildDismissedProposalStorageKey(orgId: string, viewerUserId: string | null) {
  const normalizedOrgId = cleanText(orgId) || 'org';
  const normalizedViewerUserId = cleanText(viewerUserId) || 'viewer';
  return `cybard:dismissed-service-proposals:${normalizedOrgId}:${normalizedViewerUserId}`;
}

function buildAutoProposalTitle(payload: ServiceConfigSubmission) {
  const serviceName =
    cleanText(payload.serviceName) ||
    cleanText(payload.serviceCode) ||
    'Service';
  const tierName = cleanText(payload.tierName) || 'Tier';
  if (payload.currentServiceId) {
    return payload.requestedAction === 'upgrade'
      ? `${serviceName} / ${tierName} upgrade proposal`
      : `${serviceName} / ${tierName} change proposal`;
  }
  return `${serviceName} / ${tierName} proposal`;
}

function getProposalLaneSelection(proposal: ServiceWorkspaceProposal) {
  const selections = Array.isArray(proposal.laneSelections)
    ? proposal.laneSelections
    : [];
  const tierSkuId = cleanText(proposal.tierSkuId);
  const matchByTier = selections.find((entry) => {
    const entryTier = cleanText(
      typeof entry?.tierSkuId === 'string' ? entry.tierSkuId : '',
    );
    return tierSkuId ? entryTier === tierSkuId : Boolean(entryTier);
  });
  return (matchByTier ?? selections[0] ?? null) as
    | {
        tierSkuId?: unknown;
        laneIds?: unknown;
        laneLabels?: unknown;
      }
    | null;
}

function getProposalOfferingCodes(proposal: ServiceWorkspaceProposal) {
  const selection = getProposalLaneSelection(proposal);
  const values = Array.isArray(selection?.laneIds) ? selection.laneIds : [];
  return Array.from(
    new Set(values.map((value) => cleanText(String(value ?? ''))).filter(Boolean)),
  );
}

function getProposalOfferingLabels(proposal: ServiceWorkspaceProposal) {
  const selection = getProposalLaneSelection(proposal);
  const laneLabels = Array.isArray(selection?.laneLabels)
    ? selection.laneLabels
    : [];
  const normalizedLaneLabels = laneLabels
    .map((value) => cleanText(String(value ?? '')))
    .filter(Boolean);
  if (normalizedLaneLabels.length) return normalizedLaneLabels;
  return Array.isArray(proposal.selectedOfferings)
    ? proposal.selectedOfferings.map((value) => cleanText(value)).filter(Boolean)
    : [];
}

function readProposalDiscountCode(proposal: ServiceWorkspaceProposal) {
  const snapshot = proposal.configurationSnapshot;
  if (!snapshot || typeof snapshot !== 'object') return '';
  return cleanText(
    typeof snapshot.discountCode === 'string' ? snapshot.discountCode : '',
  );
}

function buildProposalSelectionSeed(
  proposal: ServiceWorkspaceProposal,
): ClientServiceExplorerSelectionSeed {
  return {
    key: proposal.id,
    proposalId: proposal.id,
    serviceCode:
      cleanText(proposal.serviceCode) || cleanText(proposal.serviceCategory),
    serviceCategory: cleanText(proposal.serviceCategory),
    tierSkuId: cleanText(proposal.tierSkuId),
    offeringCodes: getProposalOfferingCodes(proposal),
    offeringLabels: getProposalOfferingLabels(proposal),
    discountCode: readProposalDiscountCode(proposal),
  };
}

export default function ServiceWorkspace({
  mode,
  orgId,
  organizationName,
  refreshNonce = 0,
  compact = false,
}: ServiceWorkspaceProps) {
  const stripe = useStripe();
  const [selectedOrgId, setSelectedOrgId] = useState(() => cleanText(orgId));
  const [summary, setSummary] = useState<ServiceWorkspaceSummary | null>(null);
  const [catalog, setCatalog] = useState<ServiceCatalogService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState>(null);
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [proposalActionId, setProposalActionId] = useState<string | null>(null);
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [dismissedProposalIds, setDismissedProposalIds] = useState<string[]>([]);
  const [banner, setBanner] = useState<BannerState | null>(null);
  const [searchParams] = useSearchParams();
  const seenProposalIdsRef = useRef<Set<string>>(new Set());
  const seenSessionIdRef = useRef<string | null>(null);

  const refreshWorkspace = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force === true;
    setLoading(true);
    setError(null);
    try {
      const requestedOrgId = cleanText(selectedOrgId) || cleanText(orgId);
      const [summaryResponse, catalogResponse] = await Promise.all([
        fetchServiceWorkspaceSummary(requestedOrgId || undefined, { force }),
        fetchServiceWorkspaceCatalog({ force }),
      ]);
      setSummary(summaryResponse);
      setCatalog(catalogResponse.services ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load the services workspace.');
    } finally {
      setLoading(false);
    }
  }, [orgId, selectedOrgId]);

  useEffect(() => {
    void refreshWorkspace();
  }, [refreshWorkspace, refreshNonce]);

  useEffect(() => {
    const normalizedPropOrgId = cleanText(orgId);
    if (normalizedPropOrgId && normalizedPropOrgId !== selectedOrgId) {
      setSelectedOrgId(normalizedPropOrgId);
    }
  }, [orgId, selectedOrgId]);

  useEffect(() => {
    const storageKey = buildDismissedProposalStorageKey(
      summary?.organization.id ?? selectedOrgId ?? '',
      summary?.viewer.userId ?? null,
    );
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setDismissedProposalIds(
        Array.isArray(parsed)
          ? parsed
              .map((value) => cleanText(String(value ?? '')))
              .filter(Boolean)
          : [],
      );
    } catch {
      setDismissedProposalIds([]);
    }
  }, [selectedOrgId, summary?.organization.id, summary?.viewer.userId]);

  useEffect(() => {
    if (mode !== 'client' || !summary?.viewer.canManageServices) return;
    const unseen = summary.pendingProposals.filter(
      (proposal) => proposal.status === 'sent' && !seenProposalIdsRef.current.has(proposal.id),
    );
    if (!unseen.length) return;
    unseen.forEach((proposal) => seenProposalIdsRef.current.add(proposal.id));
    void Promise.allSettled(unseen.map((proposal) => markClientServiceProposalViewed(proposal.id))).then(() => {
      void refreshWorkspace({ force: true });
    });
  }, [mode, refreshWorkspace, summary]);

  useEffect(() => {
    if (mode !== 'client') return;
    const sessionId = cleanText(searchParams.get('session_id'));
    const checkoutState = cleanText(searchParams.get('checkout'));
    if (checkoutState === 'cancelled') {
      setBanner({
        tone: 'warn',
        title: 'Checkout cancelled',
        message: 'No service changes were applied. You can review the configuration and start checkout again when you are ready.',
      });
    }
    if (!sessionId || seenSessionIdRef.current === sessionId) return;
    seenSessionIdRef.current = sessionId;
    void (async () => {
      try {
        const status = await fetchServiceCheckoutSessionStatus(sessionId);
        setBanner({
          tone:
            status.status === 'paid'
              ? 'success'
              : status.status === 'pending'
                ? 'warn'
                : status.status === 'failed' || status.status === 'expired'
                  ? 'danger'
                  : 'neutral',
          title:
            status.status === 'paid'
              ? 'Checkout confirmed'
              : status.status === 'pending'
                ? 'Checkout pending'
                : status.status === 'expired'
                  ? 'Checkout expired'
                  : status.status === 'failed'
                    ? 'Checkout failed'
                    : 'Checkout status',
          message: status.message,
        });
        if (status.status === 'paid' || status.status === 'pending') {
          await refreshWorkspace({ force: true });
        }
      } catch (err) {
        setBanner({
          tone: 'danger',
          title: 'Unable to verify checkout',
          message:
            err instanceof Error
              ? err.message
              : 'We could not verify the checkout session status yet.',
        });
      }
    })();
  }, [mode, refreshWorkspace, searchParams]);

  const canManageServices = summary?.viewer.canManageServices === true;
  const pendingProposals = useMemo(
    () => summary?.pendingProposals ?? [],
    [summary?.pendingProposals],
  );
  const visiblePendingProposals = useMemo(
    () =>
      pendingProposals.filter((proposal) => {
        const proposalId = cleanText(proposal.id);
        return proposalId && !dismissedProposalIds.includes(proposalId);
      }),
    [dismissedProposalIds, pendingProposals],
  );
  const selectedPendingProposal = useMemo(
    () =>
      visiblePendingProposals.find((proposal) => proposal.id === selectedProposalId) ??
      null,
    [selectedProposalId, visiblePendingProposals],
  );
  const proposalSelectionSeed = useMemo(
    () =>
      selectedPendingProposal
        ? buildProposalSelectionSeed(selectedPendingProposal)
        : null,
    [selectedPendingProposal],
  );
  const explorerInstanceKey = `${summary?.organization.id ?? 'org'}:${selectedProposalId ?? 'default'}`;
  const manageableOrganizations = useMemo(
    () => summary?.manageableOrganizations ?? [],
    [summary?.manageableOrganizations],
  );
  const historyProposals = useMemo(
    () => summary?.proposalHistory ?? [],
    [summary?.proposalHistory],
  );
  const currentServices = useMemo(
    () => summary?.currentServices ?? [],
    [summary?.currentServices],
  );
  const organizationLabel =
    cleanText(summary?.organization.businessName) ||
    cleanText(summary?.organization.name) ||
    cleanText(organizationName) ||
    'this account';

  useEffect(() => {
    setSelectedProposalId((current) =>
      current && visiblePendingProposals.some((proposal) => proposal.id === current)
        ? current
        : null,
    );
  }, [visiblePendingProposals]);

  useEffect(() => {
    if (mode !== 'client') return;
    const requestedProposalId = cleanText(searchParams.get('proposalId'));
    if (!requestedProposalId) return;
    const matchingProposal = pendingProposals.find(
      (proposal) => proposal.id === requestedProposalId,
    );
    if (!matchingProposal) return;
    setDismissedProposalIds((current) =>
      current.filter((proposalId) => proposalId !== requestedProposalId),
    );
    setSelectedProposalId(requestedProposalId);
  }, [mode, pendingProposals, searchParams]);

  useEffect(() => {
    const storageKey = buildDismissedProposalStorageKey(
      summary?.organization.id ?? selectedOrgId ?? '',
      summary?.viewer.userId ?? null,
    );
    const pendingIds = new Set(
      pendingProposals
        .map((proposal) => cleanText(proposal.id))
        .filter(Boolean),
    );
    const nextDismissed = dismissedProposalIds.filter((proposalId) =>
      pendingIds.has(proposalId),
    );
    if (nextDismissed.length !== dismissedProposalIds.length) {
      setDismissedProposalIds(nextDismissed);
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(nextDismissed));
    } catch {
      // Ignore local storage write errors and keep the in-memory state.
    }
  }, [
    dismissedProposalIds,
    pendingProposals,
    selectedOrgId,
    summary?.organization.id,
    summary?.viewer.userId,
  ]);

  useEffect(() => {
    if (!banner || (banner.tone !== 'success' && banner.tone !== 'neutral')) return;
    const timeoutId = window.setTimeout(() => {
      setBanner((current) =>
        current &&
        current.title === banner.title &&
        current.message === banner.message &&
        current.tone === banner.tone
          ? null
          : current,
      );
    }, 5000);
    return () => window.clearTimeout(timeoutId);
  }, [banner]);

  const currentServiceByCode = useMemo(() => {
    const map = new Map<string, ServiceWorkspaceCurrentService>();
    currentServices.forEach((service) => {
      const key = normalizeCode(service.serviceCode || service.serviceName);
      if (key && !map.has(key)) {
        map.set(key, service);
      }
    });
    return map;
  }, [currentServices]);

  const activeCatalogServices = useMemo(
    () => catalog.filter((service) => service.checkoutEnabled !== false && service.tiers.length > 0),
    [catalog],
  );

  const openServiceComposer = useCallback(
    (nextMode: 'checkout' | 'proposal', current?: ServiceWorkspaceCurrentService | null, service?: ServiceCatalogService | null) => {
      setDrawerState({
        mode: nextMode,
        initialServiceCode:
          cleanText(service?.code || service?.category) ||
          cleanText(current?.serviceCode),
        initialTierSkuId: cleanText(current?.tierSkuId),
        initialOfferingLabels: current?.selectedOfferings ?? [],
        initialAddOnNames: current?.addOnNames ?? [],
        initialTitle:
          nextMode === 'proposal' && service
            ? `${cleanText(service.title) || cleanText(service.code) || 'Service'} proposal`
            : null,
        initialSummary: null,
        initialProposalType: current ? 'change_service' : 'new_service',
      });
    },
    [],
  );

  const launchHostedCheckout = useCallback((checkoutUrl: string | null) => {
    if (!checkoutUrl) {
      throw new Error('Checkout URL was not returned by the server.');
    }
    window.location.assign(checkoutUrl);
  }, []);

  const buildClientUrls = useCallback(() => {
    const baseUrl = window.location.origin;
    const path = '/dashboard/services';
    return {
      successUrl: `${baseUrl}${path}?session_id={CHECKOUT_SESSION_ID}&checkout=success`,
      cancelUrl: `${baseUrl}${path}?checkout=cancelled`,
      returnUrl: `${baseUrl}${path}`,
    };
  }, []);

  const handleClientCheckout = useCallback(
    async (payload: ServiceConfigSubmission) => {
      if (!summary) return;
      setDrawerBusy(true);
      setError(null);
      try {
        if (
          cleanText(payload.currentServiceId) &&
          payload.requestedAction &&
          payload.requestedAction !== 'purchase'
        ) {
          const mutationPayload = {
            orgId: summary.organization.id,
            currentServiceId: cleanText(payload.currentServiceId),
            tierSkuId: cleanText(payload.tierSkuId),
            laneSelections: payload.laneSelections,
            discountCode: cleanText(payload.discountCode) || undefined,
            paymentMethodId: cleanText(payload.paymentMethodId) || undefined,
            prorationDate:
              typeof payload.prorationDate === 'number' &&
              Number.isFinite(payload.prorationDate) &&
              payload.prorationDate > 0
                ? Math.floor(payload.prorationDate)
                : undefined,
          };
          if (!mutationPayload.tierSkuId) {
            throw new Error('A service tier must be selected before continuing.');
          }

          if (payload.requestedAction === 'update') {
            const result = await changeCurrentClientService(mutationPayload);
            if (result.status === 'no_change') {
              setBanner({
                tone: 'neutral',
                title: 'No change detected',
                message:
                  'The selected configuration already matches the active service on this account.',
              });
            } else {
              setBanner({
                tone: 'neutral',
                title: 'Service updated',
                message:
                  'The active service configuration was updated successfully.',
              });
            }
            await refreshWorkspace({ force: true });
            return;
          }

          const upgrade = await changeCurrentClientService(mutationPayload);
          if (upgrade.status === 'requires_action') {
            if (!upgrade.clientSecret) {
              throw new Error('Stripe did not return a payment confirmation token.');
            }
            if (!stripe) {
              throw new Error(
                'Payments are still initializing. Please try again in a moment.',
              );
            }
            const confirmation = await stripe.confirmCardPayment(
              upgrade.clientSecret,
            );
            if (confirmation.error) {
              throw confirmation.error;
            }
            await finalizeCurrentClientServiceChange({
              ...mutationPayload,
              paymentIntentId:
                confirmation.paymentIntent?.id ??
                upgrade.paymentIntentId ??
                undefined,
            });
          }

          setBanner({
            tone: 'neutral',
            title: 'Service upgraded',
            message:
              'The active service was upgraded and the account snapshot has been refreshed.',
          });
          await refreshWorkspace({ force: true });
          return;
        }

        const urls = buildClientUrls();
        const checkout = await createClientServiceCheckoutSession({
          orgId: summary.organization.id,
          cartItems: payload.cartItems,
          laneSelections: payload.laneSelections,
          discountCode: cleanText(payload.discountCode),
          successUrl: urls.successUrl,
          cancelUrl: urls.cancelUrl,
          returnUrl: urls.returnUrl,
          uiMode: 'hosted',
        });
        launchHostedCheckout(checkout.checkoutUrl);
      } catch (err) {
        const nextError =
          err instanceof Error
            ? err
            : new Error('Unable to continue with this service configuration.');
        setError(nextError.message);
        throw nextError;
      } finally {
        setDrawerBusy(false);
      }
    },
    [buildClientUrls, launchHostedCheckout, refreshWorkspace, stripe, summary],
  );

  const handleStaffProposalBuilder = useCallback(
    async (payload: ServiceConfigSubmission) => {
      if (!summary) return;
      setDrawerBusy(true);
      setError(null);
      try {
        await createClientServiceProposal({
          orgId: summary.organization.id,
          title: buildAutoProposalTitle(payload),
          proposalType: payload.currentServiceId ? 'change_service' : 'new_service',
          serviceCategory: payload.serviceCategory,
          serviceCode: payload.serviceCode,
          serviceName: payload.serviceName,
          tierSkuId: payload.tierSkuId,
          tierName: payload.tierName,
          cartItems: payload.cartItems,
          laneSelections: payload.laneSelections,
          configurationSnapshot: payload.configurationSnapshot,
        });
        setBanner({
          tone: 'success',
          title: 'Proposal sent',
          message:
            'The proposal is now available in the client portal services workspace.',
        });
        await refreshWorkspace({ force: true });
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to create this service proposal.',
        );
      } finally {
        setDrawerBusy(false);
      }
    },
    [refreshWorkspace, summary],
  );

  const handleDrawerSubmit = useCallback(
    async (payload: ServiceConfigSubmission) => {
      if (!summary || !drawerState) return;
      setDrawerBusy(true);
      setError(null);
      try {
        if (drawerState.mode === 'proposal') {
          await createClientServiceProposal({
            orgId: summary.organization.id,
            title: payload.title || 'Service proposal',
            summary: payload.summary,
            proposalType: payload.proposalType,
            serviceCategory: payload.serviceCategory,
            serviceCode: payload.serviceCode,
            serviceName: payload.serviceName,
            tierSkuId: payload.tierSkuId,
            tierName: payload.tierName,
            cartItems: payload.cartItems,
            laneSelections: payload.laneSelections,
            configurationSnapshot: payload.configurationSnapshot,
            expiresAt: payload.expiresAt,
          });
          setDrawerState(null);
          setBanner({
            tone: 'success',
            title: 'Proposal sent',
            message: 'The service proposal is now available in the client services workspace.',
          });
          await refreshWorkspace({ force: true });
          return;
        }

        const urls = buildClientUrls();
        const checkout = await createClientServiceCheckoutSession({
          orgId: summary.organization.id,
          cartItems: payload.cartItems,
          laneSelections: payload.laneSelections,
          successUrl: urls.successUrl,
          cancelUrl: urls.cancelUrl,
          returnUrl: urls.returnUrl,
          uiMode: 'hosted',
        });
        launchHostedCheckout(checkout.checkoutUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to continue with this service configuration.');
      } finally {
        setDrawerBusy(false);
      }
    },
    [buildClientUrls, drawerState, launchHostedCheckout, refreshWorkspace, summary],
  );

  const handleCancelProposal = useCallback(async (proposal: ServiceWorkspaceProposal) => {
    if (!window.confirm(`Cancel "${proposal.title}"? The client will no longer be able to act on it.`)) return;
    setProposalActionId(proposal.id);
    setError(null);
    try {
      await cancelClientServiceProposal(proposal.id);
      await refreshWorkspace({ force: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to cancel this proposal.');
    } finally {
      setProposalActionId(null);
    }
  }, [refreshWorkspace]);

  const handleDeclineProposal = useCallback(async (proposal: ServiceWorkspaceProposal) => {
    setProposalActionId(proposal.id);
    setError(null);
    try {
      await rejectClientServiceProposal(proposal.id);
      setDismissedProposalIds((current) =>
        current.filter((proposalId) => proposalId !== cleanText(proposal.id)),
      );
      setSelectedProposalId((current) =>
        current === proposal.id ? null : current,
      );
      setBanner({
        tone: 'neutral',
        title: 'Proposal declined',
        message: 'The proposal was declined and removed from the client services workspace.',
      });
      await refreshWorkspace({ force: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to decline this proposal.');
    } finally {
      setProposalActionId(null);
    }
  }, [refreshWorkspace]);

  const dismissPendingProposal = useCallback((proposalId: string) => {
    const normalizedProposalId = cleanText(proposalId);
    if (!normalizedProposalId) return;
    setDismissedProposalIds((current) =>
      current.includes(normalizedProposalId)
        ? current
        : [...current, normalizedProposalId],
    );
    setSelectedProposalId((current) =>
      current === normalizedProposalId ? null : current,
    );
  }, []);

  if (loading && !summary) {
    return (
      <Shell $compact={compact}>
        {!compact ? (
          <WorkspaceSkeletonPanel>
            <div style={{ display: 'grid', gap: 8 }}>
              <Skeleton $width="112px" $height="12px" />
              <Skeleton $width="38%" $height="30px" />
              <Skeleton $width="78%" />
              <Skeleton $width="72%" />
            </div>
          </WorkspaceSkeletonPanel>
        ) : null}

        <Section>
          <SectionHeader>
            <SectionTitleWrap>
              <SectionTitle>Current services</SectionTitle>
              <SectionSub>Loading current services...</SectionSub>
            </SectionTitleWrap>
          </SectionHeader>
          <WorkspaceSkeletonGrid>
            {Array.from({ length: 2 }, (_, index) => (
              <WorkspaceSkeletonCard key={`service-workspace-current-skeleton-${index}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'grid', gap: 8, flex: '1 1 auto' }}>
                    <Skeleton $width={index === 0 ? '58%' : '46%'} $height="20px" />
                    <Skeleton $width="34%" $height="12px" />
                  </div>
                  <Skeleton $width="86px" $height="28px" $radius="999px" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  <Skeleton $height="56px" $radius="14px" />
                  <Skeleton $height="56px" $radius="14px" />
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  <Skeleton $width="108px" $height="12px" />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Skeleton $width="88px" $height="26px" $radius="999px" />
                    <Skeleton $width="96px" $height="26px" $radius="999px" />
                    <Skeleton $width="74px" $height="26px" $radius="999px" />
                  </div>
                </div>
                <Skeleton $width="178px" $height="38px" $radius="12px" />
              </WorkspaceSkeletonCard>
            ))}
          </WorkspaceSkeletonGrid>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitleWrap>
              <SectionTitle>Pending proposals</SectionTitle>
              <SectionSub>Loading pending proposals...</SectionSub>
            </SectionTitleWrap>
          </SectionHeader>
          <div style={{ display: 'grid', gap: 14 }}>
            {Array.from({ length: 2 }, (_, index) => (
              <WorkspaceSkeletonCard key={`service-workspace-proposal-skeleton-${index}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'grid', gap: 8, flex: '1 1 auto' }}>
                    <Skeleton $width={index === 0 ? '44%' : '52%'} $height="18px" />
                    <Skeleton $width="36%" $height="12px" />
                  </div>
                  <Skeleton $width="92px" $height="28px" $radius="999px" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
                  <Skeleton $height="58px" $radius="14px" />
                  <Skeleton $height="58px" $radius="14px" />
                  <Skeleton $height="58px" $radius="14px" />
                  <Skeleton $height="58px" $radius="14px" />
                </div>
              </WorkspaceSkeletonCard>
            ))}
          </div>
        </Section>
      </Shell>
    );
  }

  if (mode === 'client') {
    return (
      <Shell $compact={compact}>
        {banner ? (
          <Banner $tone={banner.tone}>
            <BannerTitle>{banner.title}</BannerTitle>
            <div>{banner.message}</div>
          </Banner>
        ) : null}
        {mode === 'client' && !canManageServices ? (
          <Notice>
            Only the primary account holder can purchase services, accept proposals, or make contract changes. Other organization members have read-only visibility here.
          </Notice>
        ) : null}
        {error ? <Notice $tone="danger">{error}</Notice> : null}
        {canManageServices && visiblePendingProposals.length ? (
          <ProposalAwaitList>
            {visiblePendingProposals.map((proposal) => {
              const isActive = proposal.id === selectedProposalId;
              const serviceLabel =
                cleanText(proposal.serviceName) ||
                cleanText(proposal.serviceCode) ||
                'Service';
              const tierLabel = cleanText(proposal.tierName) || 'Tier';
              const proposalDate = formatDateLabel(
                proposal.sentAt || proposal.createdAt,
              );
              return (
                <ProposalAwaitCard
                  key={proposal.id}
                  $active={isActive}
                >
                  <ProposalDismissButton
                    type="button"
                    aria-label={`Dismiss ${serviceLabel} proposal`}
                    onClick={(event) => {
                      event.stopPropagation();
                      dismissPendingProposal(proposal.id);
                    }}
                  >
                    <X size={13} />
                  </ProposalDismissButton>
                  <ProposalAwaitBody
                    type="button"
                    onClick={() => {
                      setSelectedProposalId(proposal.id);
                      setBanner(null);
                      setError(null);
                    }}
                    style={{ paddingRight: 118, minHeight: 58 }}
                  >
                    <ProposalAwaitEyebrow>A proposal awaits</ProposalAwaitEyebrow>
                    <ProposalAwaitTitle>
                      {serviceLabel} / {tierLabel}
                    </ProposalAwaitTitle>
                    <ProposalAwaitCopy>
                      {proposal.summary
                        ? proposal.summary
                        : `Sent ${proposalDate}. Click to load this configuration into the services workspace.`}
                    </ProposalAwaitCopy>
                  </ProposalAwaitBody>
                  <ProposalDeclineButton
                    type="button"
                    disabled={proposalActionId === proposal.id}
                    onClick={() => {
                      void handleDeclineProposal(proposal);
                    }}
                  >
                    {proposalActionId === proposal.id ? 'Declining…' : 'Decline'}
                  </ProposalDeclineButton>
                </ProposalAwaitCard>
              );
            })}
          </ProposalAwaitList>
        ) : null}
        {canManageServices &&
        manageableOrganizations.length ? (
          <OrgSelectorRow>
            <OrgSelectorControl>
              <OrgSelectorLabel htmlFor="client-services-org-selector">
                Organization
              </OrgSelectorLabel>
              <OrgSelectorField
                id="client-services-org-selector"
                value={cleanText(selectedOrgId) || summary?.organization.id || ''}
                onChange={(event) => {
                  setSelectedOrgId(cleanText(event.target.value));
                  setSelectedProposalId(null);
                  setBanner(null);
                  setError(null);
                }}
                disabled={manageableOrganizations.length <= 1 || drawerBusy}
              >
                {manageableOrganizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {getOrganizationOptionLabel(organization)}
                  </option>
                ))}
              </OrgSelectorField>
            </OrgSelectorControl>
          </OrgSelectorRow>
        ) : null}
        <ClientServiceExplorer
          key={explorerInstanceKey}
          orgId={summary?.organization.id ?? ''}
          services={activeCatalogServices}
          currentServices={currentServices}
          canManageServices={canManageServices}
          checkoutBusy={drawerBusy}
          selectionSeed={proposalSelectionSeed}
          onCheckout={handleClientCheckout}
        />
      </Shell>
    );
  }

  if (mode === 'staff') {
    return (
      <Shell $compact={compact}>
        {banner ? (
          <Banner $tone={banner.tone}>
            <BannerTitle>{banner.title}</BannerTitle>
            <div>{banner.message}</div>
          </Banner>
        ) : null}
        {error ? <Notice $tone="danger">{error}</Notice> : null}
        <EmbeddedExplorerFrame $compact={compact}>
          <ClientServiceExplorer
            orgId={summary?.organization.id ?? ''}
            services={activeCatalogServices}
            currentServices={currentServices}
            canManageServices={canManageServices}
            actionMode="proposal"
            checkoutBusy={drawerBusy}
            onCheckout={handleStaffProposalBuilder}
          />
        </EmbeddedExplorerFrame>
      </Shell>
    );
  }

  return (
    <Shell $compact={compact}>
      {!compact ? (
        <HeaderCard>
          <HeaderRow>
            <HeaderTitleWrap>
              <Eyebrow>Service Workspace</Eyebrow>
              <Title>{organizationLabel}</Title>
              <Sub>
                Review current client services, create proposals, and manage the commercial service workspace for this organization.
              </Sub>
            </HeaderTitleWrap>
            <HeaderActions>
              {mode === 'staff' ? (
                <ActionButton type="button" $accent onClick={() => openServiceComposer('proposal')}>
                  <FileText size={14} />
                  Create proposal
                </ActionButton>
              ) : null}
            </HeaderActions>
          </HeaderRow>
          {banner ? (
            <Banner $tone={banner.tone}>
              <BannerTitle>{banner.title}</BannerTitle>
              <div>{banner.message}</div>
            </Banner>
          ) : null}
          {error ? <Notice $tone="danger">{error}</Notice> : null}
        </HeaderCard>
      ) : (
        <>
          {banner ? (
            <Banner $tone={banner.tone}>
              <BannerTitle>{banner.title}</BannerTitle>
              <div>{banner.message}</div>
            </Banner>
          ) : null}
          {error ? <Notice $tone="danger">{error}</Notice> : null}
        </>
      )}

      <Section>
        <SectionHeader>
          <SectionTitleWrap>
            <SectionTitle>Current services</SectionTitle>
            <SectionSub>Active service lines on this organization, including tier, contract band, and included work lanes.</SectionSub>
          </SectionTitleWrap>
          <ActionButton type="button" onClick={() => openServiceComposer('proposal')}>
            <FileText size={14} />
            New proposal
          </ActionButton>
        </SectionHeader>

        {currentServices.length ? (
          <Grid>
            {currentServices.map((service) => (
              <ServiceCard key={service.id}>
                <ServiceCardHeader>
                  <div>
                    <ServiceName>{cleanText(service.serviceName) || cleanText(service.serviceCode) || 'Service line'}</ServiceName>
                    <ServiceMeta>{cleanText(service.tierName) || 'Tier not set'}{service.contractTerm ? ` / ${service.contractTerm}` : ''}</ServiceMeta>
                  </div>
                  <StatusPill $tone={serviceStatusTone(service.status)}>{cleanText(service.status) || 'active'}</StatusPill>
                </ServiceCardHeader>

                <MetaGrid>
                  <MetaCell>
                    <MetaLabel>Capacity band</MetaLabel>
                    <MetaValue>{formatHoursBand(service.contractedMonthlyMinutesMin, service.contractedMonthlyMinutesMax)}</MetaValue>
                  </MetaCell>
                  <MetaCell>
                    <MetaLabel>Start</MetaLabel>
                    <MetaValue>{formatDateLabel(service.startsAt)}</MetaValue>
                  </MetaCell>
                </MetaGrid>

                {service.selectedOfferings.length ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <MetaLabel>Included offerings</MetaLabel>
                    <PillRow>
                      {service.selectedOfferings.map((offering) => (
                        <Pill key={offering}>{offering}</Pill>
                      ))}
                    </PillRow>
                  </div>
                ) : null}

                {service.addOnNames.length ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <MetaLabel>Add-ons</MetaLabel>
                    <PillRow>
                      {service.addOnNames.map((addOn) => (
                        <Pill key={addOn}>{addOn}</Pill>
                      ))}
                    </PillRow>
                  </div>
                ) : null}

                <ServiceCardActions>
                  <ActionButton type="button" $accent onClick={() => openServiceComposer('proposal', service)}>
                    <FileText size={14} />
                    Create change proposal
                  </ActionButton>
                </ServiceCardActions>
              </ServiceCard>
            ))}
          </Grid>
        ) : (
          <Notice>No active service lines are attached to this organization yet.</Notice>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitleWrap>
            <SectionTitle>Pending proposals</SectionTitle>
            <SectionSub>Commercial proposals that are still awaiting a decision or checkout completion.</SectionSub>
          </SectionTitleWrap>
        </SectionHeader>

        {pendingProposals.length ? (
          <ProposalList>
            {pendingProposals.map((proposal) => (
              <ProposalCard key={proposal.id}>
                <ProposalHeader>
                  <div>
                    <ProposalTitle>{proposal.title}</ProposalTitle>
                    <ProposalSub>
                      {cleanText(proposal.serviceName) || cleanText(proposal.serviceCode) || 'Service'} / {cleanText(proposal.tierName) || 'Tier'}
                      {proposal.expiresAt ? ` / Expires ${formatDateLabel(proposal.expiresAt)}` : ''}
                    </ProposalSub>
                  </div>
                  <StatusPill $tone={proposalTone(proposal.status)}>{proposal.status.replace(/_/g, ' ')}</StatusPill>
                </ProposalHeader>

                {proposal.summary ? <Notice>{proposal.summary}</Notice> : null}

                <ProposalMetrics>
                  <ProposalMetric>
                    <ProposalMetricLabel>Monthly</ProposalMetricLabel>
                    <ProposalMetricValue>{formatCurrency(proposal.recurringMonthlyCents)}</ProposalMetricValue>
                  </ProposalMetric>
                  <ProposalMetric>
                    <ProposalMetricLabel>Due now</ProposalMetricLabel>
                    <ProposalMetricValue style={{ fontSize: proposal.dueNowDynamic ? 13 : undefined }}>
                      {proposal.dueNowDynamic ? 'Calculated at review' : formatCurrency(proposal.totalDueNowCents)}
                    </ProposalMetricValue>
                  </ProposalMetric>
                  <ProposalMetric>
                    <ProposalMetricLabel>Sent</ProposalMetricLabel>
                    <ProposalMetricValue style={{ fontSize: 13 }}>{formatDateLabel(proposal.sentAt || proposal.createdAt)}</ProposalMetricValue>
                  </ProposalMetric>
                  <ProposalMetric>
                    <ProposalMetricLabel>Type</ProposalMetricLabel>
                    <ProposalMetricValue style={{ fontSize: 13 }}>{proposal.proposalType.replace(/_/g, ' ')}</ProposalMetricValue>
                  </ProposalMetric>
                </ProposalMetrics>

                {proposal.selectedOfferings.length ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <MetaLabel>Offerings</MetaLabel>
                    <PillRow>
                      {proposal.selectedOfferings.map((offering) => (
                        <Pill key={offering}>{offering}</Pill>
                      ))}
                    </PillRow>
                  </div>
                ) : null}

                {proposal.addOnNames.length ? (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <MetaLabel>Add-ons</MetaLabel>
                    <PillRow>
                      {proposal.addOnNames.map((addOn) => (
                        <Pill key={addOn}>{addOn}</Pill>
                      ))}
                    </PillRow>
                  </div>
                ) : null}

                <ServiceCardActions>
                  <ActionButton
                    type="button"
                    $danger
                    disabled={proposalActionId === proposal.id}
                    onClick={() => void handleCancelProposal(proposal)}
                  >
                    <XCircle size={14} />
                    {proposalActionId === proposal.id ? 'Working...' : 'Cancel proposal'}
                  </ActionButton>
                </ServiceCardActions>
              </ProposalCard>
            ))}
          </ProposalList>
        ) : (
          <Notice>No pending service proposals are waiting on this organization right now.</Notice>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitleWrap>
            <SectionTitle>Create proposals from catalog</SectionTitle>
            <SectionSub>
              Use the published service catalog to create accurate proposals for this organization.
            </SectionSub>
          </SectionTitleWrap>
        </SectionHeader>

        {activeCatalogServices.length ? (
          <CatalogGrid>
            {activeCatalogServices.map((service) => {
              const currentService = currentServiceByCode.get(getServiceKey(service)) ?? null;
              const startingPrice = getTierStartingPrice(service);
              return (
                <CatalogCard key={getServiceKey(service)}>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <CatalogTitle>{cleanText(service.title) || cleanText(service.code) || 'Service'}</CatalogTitle>
                    <CatalogCopy>{cleanText(service.description) || cleanText(service.subtitle) || 'Published service offering.'}</CatalogCopy>
                  </div>
                  <MetaGrid>
                    <MetaCell>
                      <MetaLabel>Starting at</MetaLabel>
                      <MetaValue>{startingPrice !== null ? formatCurrency(startingPrice) : '--'}</MetaValue>
                    </MetaCell>
                    <MetaCell>
                      <MetaLabel>Tiers</MetaLabel>
                      <MetaValue>{service.tiers.length}</MetaValue>
                    </MetaCell>
                  </MetaGrid>
                  <PillRow>
                    {currentService ? <Pill>Current service</Pill> : null}
                    {service.tiers.slice(0, 3).map((tier) => (
                      <Pill key={cleanText(tier.skuId) || cleanText(tier.name)}>
                        {cleanText(tier.name) || 'Tier'}
                      </Pill>
                    ))}
                  </PillRow>
                  <ServiceCardActions>
                    <ActionButton
                      type="button"
                      $accent
                      onClick={() => openServiceComposer('proposal', currentService, service)}
                    >
                      <FileText size={14} />
                      {currentService ? 'Propose change' : 'Create proposal'}
                    </ActionButton>
                  </ServiceCardActions>
                </CatalogCard>
              );
            })}
          </CatalogGrid>
        ) : (
          <Notice>No published service catalog entries are available yet.</Notice>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitleWrap>
            <SectionTitle>Proposal history</SectionTitle>
            <SectionSub>Accepted, rejected, canceled, or expired proposals for this organization.</SectionSub>
          </SectionTitleWrap>
        </SectionHeader>

        {historyProposals.length ? (
          <HistoryList>
            {historyProposals.slice(0, 10).map((proposal) => (
              <HistoryRow key={proposal.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ color: '#f3f4f6', fontSize: 13, fontWeight: 900 }}>{proposal.title}</div>
                  <StatusPill $tone={proposalTone(proposal.status)}>{proposal.status.replace(/_/g, ' ')}</StatusPill>
                </div>
                <div style={{ color: '#9aa4b2', fontSize: 11, lineHeight: 1.55, fontWeight: 700 }}>
                  {cleanText(proposal.serviceName) || cleanText(proposal.serviceCode) || 'Service'} / {cleanText(proposal.tierName) || 'Tier'} / {formatDateLabel(proposal.updatedAt || proposal.createdAt)}
                </div>
              </HistoryRow>
            ))}
          </HistoryList>
        ) : (
          <Notice>No prior service proposal history exists for this organization yet.</Notice>
        )}
      </Section>

      <ServiceConfigDrawer
        open={mode === 'staff' && Boolean(drawerState)}
        mode={drawerState?.mode ?? 'proposal'}
        orgName={organizationLabel}
        services={activeCatalogServices}
        submitting={drawerBusy}
        initialServiceCode={drawerState?.initialServiceCode}
        initialTierSkuId={drawerState?.initialTierSkuId}
        initialOfferingLabels={drawerState?.initialOfferingLabels}
        initialAddOnNames={drawerState?.initialAddOnNames}
        initialTitle={drawerState?.initialTitle}
        initialSummary={drawerState?.initialSummary}
        initialProposalType={drawerState?.initialProposalType}
        submitLabel={
          drawerState?.mode === 'proposal'
            ? 'Send proposal'
            : 'Start checkout'
        }
        onClose={() => setDrawerState(null)}
        onSubmit={handleDrawerSubmit}
      />
    </Shell>
  );
}
